import { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DateTime } from "luxon"

import { getKPIRequest } from "@/api/getKPIRequest.ts"
import DateTimePicker from "@/components/DateTimePicker.tsx"
import LocationSelector from "@/components/LocationSelector.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Dialog, DialogTrigger } from "@/components/ui/dialog.tsx"
import { Label } from "@/components/ui/label.tsx"
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area.tsx"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group.tsx"
import type { KPIAggregationType, KPIGroupRequest } from "@/types/kpiTypes.ts"

import { getDates } from "../../../util/getDates.ts"
import { exportKPIs } from "../api/exportKPIs.ts"
import KPIRequests from "../config/config.ts"

import KPIBrowserTable from "./KPIBrowserTable.tsx"
import KPIExportDialog from "./KPIExportDialog.tsx"

export default function KPIBrowser() {
  const [request, setRequest] = useState<KPIGroupRequest>({
    kpis: KPIRequests.get(2) || [],
    returnSize: 15,
    aggType: "Daily",
    asOf: DateTime.now().startOf("day").minus({ days: 1 }).toJSDate(),
  })
  const [location, setLocation] = useState<number>(2)

  const { data, isPending, isError } = useQuery({
    queryKey: ["kpiBrowser", request],
    queryFn: () => getKPIRequest(request),
  })

  const filteredData =
    data && Array.isArray(data)
      ? data.map((kpi) => ({
          ...kpi,
          values: kpi.values?.filter((v) => v.type == "Actual" || v.type == "Budget"),
        }))
      : []

  const handleLocation = (id: number) => {
    setLocation(id)
    setRequest({ ...request, kpis: KPIRequests.get(id) || [] })
  }

  const handleMove = (amount: number) => {
    let newDate = DateTime.fromJSDate(request.asOf || new Date())
    switch (request.aggType) {
      case "Daily":
        newDate = newDate.plus({ days: amount * 15 })
        break
      case "Weekly":
        newDate = newDate.plus({ weeks: amount * 15 })
        break
      case "Monthly":
        newDate = newDate.plus({ months: amount * 15 })
        break
    }
    setRequest({ ...request, asOf: newDate.toJSDate() })
  }

  const dates = getDates(request, request.asOf)

  if (isError)
    return (
      <div
        className="w-full rounded-md shadow-sm border p-4"
        style={{ height: `${52 + 52.5 * request.kpis.length}px` }}
      >
        <span className="text-status-bad font-semibold">Network Error</span>
      </div>
    )

  return (
    <>
      <div className="mb-2 justify-between flex items-center gap-4 flex-wrap">
        <div className="flex gap-2 w-full md:w-auto">
          <ToggleGroup
            type="single"
            className="shadow-sm border"
            value={request.aggType}
            onValueChange={(value: KPIAggregationType) =>
              setRequest({ ...request, aggType: value })
            }
          >
            <ToggleGroupItem
              value="Daily"
              className="p-4 data-[state=on]:bg-muted data-[state=on]:text-foreground"
            >
              Daily
            </ToggleGroupItem>
            <ToggleGroupItem
              value="Weekly"
              className="p-4 data-[state=on]:bg-muted data-[state=on]:text-foreground"
            >
              Weekly
            </ToggleGroupItem>
            <ToggleGroupItem
              value="Monthly"
              className="p-4 data-[state=on]:bg-muted data-[state=on]:text-foreground"
            >
              Monthly
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="grow-1 md:grow-0">
            <Button
              variant="outline"
              className="h-full rounded-r-none shadow-sm w-1/2"
              onClick={() => handleMove(-1)}
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="outline"
              className="h-full rounded-l-none border-l-0 shadow-sm w-1/2"
              onClick={() => handleMove(1)}
            >
              <ChevronRight />
            </Button>
          </div>
          <div className="grow-1 md:grow-0">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" className="h-full shadow-sm" onClick={() => {}}>
                  Export
                </Button>
              </DialogTrigger>
              <KPIExportDialog onExport={(data) => exportKPIs(location, data.start, data.end)} />
            </Dialog>
          </div>
        </div>
        <div className="flex gap-2 basis-full md:basis-1/3">
          <div className="basis-5/12 flex gap-2">
            <Label className="text-muted-foreground basis-12">As of:</Label>
            <DateTimePicker
              value={request.asOf}
              onChange={(d) => setRequest({ ...request, asOf: d || new Date() })}
              dateOnly={true}
              className="grow"
            />
          </div>
          <div className="basis-[200px] grow">
            <LocationSelector value={location} onChange={handleLocation} />
          </div>
        </div>
      </div>
      <div className="w-full flex">
        <ScrollArea type="always" className="w-1 flex-1 rounded-md border shadow-sm">
          <ScrollBar orientation="horizontal" className="w-full" />
          <KPIBrowserTable
            dates={dates}
            data={filteredData || []}
            paddingArray={request.kpis.map((kpi) => kpi.padding || 16)}
            isLoading={isPending}
            rows={request.kpis.length}
          />
        </ScrollArea>
      </div>
    </>
  )
}
